import './section.less';

import React from 'react';
import classnames from 'classnames';
import b_ from 'b_';

import { Toolbar } from 'controls/react/layout/toolbar';
import { Overlay } from 'controls/react/layout/overlay';

import ReactHelpers from 'tools/helpers/react';

const b = b_.with('section');

export type SectionAppearance = 'card' | 'frame' | 'frame-top-only' | 'none';
export type SectionLayout = 'stack' | 'equal' | 'sidebar-left' | 'sidebar-right';
export type SectionWidth = 'half' | 'full' | 'threeQuarter' | 'quarter' | 'oneThird' | number;
// the flex values (fit, fit-expand-only, size1) are for the code already using them; grow and shrink replace them
export type SectionHeight = 'size1' | 'fit' | 'default' | 'fit-expand-only' | 'full' | number;
export type SectionAlign = 'center' | 'flex-start' | 'flex-end' | 'baseline' | 'stretch';

export type SectionProps = {
	title?: React.ReactNode;
	appearance?: SectionAppearance;
	layout?: SectionLayout;
	direction?: 'row' | 'column';
	containerClass?: string;
	className?: string;
	contentClass?: string;
	containerRef?: React.Ref<HTMLDivElement>;
	contentRef?: React.Ref<HTMLDivElement>;
	alignItems?: SectionAlign;
	// across the parent's direction, instead of the parent's alignItems
	alignSelf?: SectionAlign;
	// along the parent's direction: takes a share of the room left over; a number is its weight among the growing siblings
	grow?: boolean | number;
	// along the parent's direction: may get smaller than its content when there is not enough room
	shrink?: boolean;
	hasFooter?: boolean;
	contentPadding?: boolean;
	childrenPadding?: boolean;
	scrollable?: boolean | 'y' | 'x';
	contentHidden?: boolean;
	display?: string;
	height?: SectionHeight;
	width?: SectionWidth;
	margin?: 'top' | 'bottom' | 'none';
	contentOverlay?: boolean;
	containerOverlay?: boolean;
	overlaySpinner?: boolean;
	overlayMessage?: string;
	onClick?: React.MouseEventHandler<HTMLDivElement>;
	hasHeader?: boolean;
	disabled?: boolean;
	hidden?: boolean;
	style?: React.CSSProperties;
	contentStyle?: React.CSSProperties;
	children?: React.ReactNode;
};

export class Section extends React.PureComponent<SectionProps> {
	static defaultProps = {
		margin: 'none',
		layout: 'stack',
		contentHidden: false,
		hasHeader: true,
		alignItems: 'stretch',
	};

	containerRef: React.Ref<HTMLDivElement>;
	contentRef: React.Ref<HTMLDivElement>;

	constructor(props: SectionProps) {
		super(props);
		this.containerRef = props.containerRef || React.createRef();
		this.contentRef = props.contentRef || React.createRef();
	}

	render() {
		const hasFooter = this.props.hasFooter || false;
		const sorted = ReactHelpers.sortOutContent(this.props.children, {
			header: this.props.hasHeader,
			footer: hasFooter,
		}) as {
			header: React.ReactElement<{ containerClass?: string; title?: React.ReactNode }> | null;
			footer: React.ReactNode;
			others: React.ReactNode[];
		};
		const { footer, others } = sorted;
		let header = sorted.header;

		if (header != null) {
			header = React.cloneElement(header, {
				containerClass: classnames(header.props.containerClass, b('header')),
				title: this.props.title || header.props.title,
			});
		}

		if (header == null && this.props.title != null) {
			header = <Toolbar title={this.props.title} containerClass={b('header')} />;
		}

		const componentClasses = b({
			'content-padding': this.getContentPadding(),
			'height': typeof this.getHeight() == 'number' ? null : this.getHeight(),
			'grow': this.props.grow === true,
			'shrink': this.props.shrink,
			'align-self': this.props.alignSelf,
			'width': !Number.isInteger(this.props.width) ? this.props.width : null,
			'appearance': this.getAppearance(),
			'half': this.props.width == 'half',
			'threeQuarter': this.props.width == 'threeQuarter',
			'quarter': this.props.width == 'quarter',
			'oneThird': this.props.width == 'oneThird',
			'margin': this.props.margin,
			'hidden': this.props.hidden,
			'has-overlay': this.props.containerOverlay || this.props.disabled,
		});

		const contentClasses = classnames(
			b('content', {
				'scrollable': this.props.scrollable,
				'direction': this.getDirection(),
				'layout': this.props.layout,
				'children-padding': this.getChildrenPadding(),
				'hidden': this.props.contentHidden,
				'display': this.props.display,
				'align-items': this.props.alignItems,
				'has-overlay': this.props.contentOverlay == true && !this.props.containerOverlay,
			}),
			{ [this.props.contentClass]: this.props.contentClass != null },
		);

		return (
			<div
				className={classnames(componentClasses, this.props.containerClass, this.props.className)}
				style={this.getContainerStyle()}
				onClick={this.props.onClick}
				ref={this.containerRef}
			>
				{header}

				<div className={contentClasses} ref={this.contentRef} style={this.props.contentStyle}>
					{others}
					{((this.props.contentOverlay == true && !this.props.containerOverlay) || this.props.disabled) && (
						<Overlay
							show={this.props.contentOverlay || this.props.disabled}
							spinner={this.props.overlaySpinner && !this.props.disabled}
							message={this.props.overlayMessage}
						/>
					)}
				</div>

				{footer}

				{this.props.containerOverlay == true && (
					<Overlay
						show={this.props.containerOverlay}
						spinner={this.props.overlaySpinner}
						message={this.props.overlayMessage}
					/>
				)}
			</div>
		);
	}

	getAppearance(): SectionAppearance {
		if (this.props.appearance) return this.props.appearance;

		if (this.isSidebarLayout()) return 'none';

		return 'card';
	}

	getHeight(): SectionHeight {
		if (this.props.height != null) return this.props.height;

		if (this.isSidebarLayout()) return 'fit-expand-only';

		return undefined;
	}

	getContainerStyle(): React.CSSProperties {
		const style = { ...this.props.style };

		if (Number.isInteger(this.props.width)) {
			style.width = this.props.width + 'px';
		}

		if (typeof this.props.height == 'number') {
			style.height = this.props.height + 'px';
		}

		// a weight; true is set by the class
		if (typeof this.props.grow == 'number') {
			style.flexGrow = this.props.grow;
		}

		return style;
	}

	getDirection(): 'row' | 'column' {
		if (this.isSidebarLayout()) return 'row';

		if (this.props.direction != null) return this.props.direction;

		return 'column';
	}

	getContentPadding(): boolean {
		if (this.props.contentPadding != null) return this.props.contentPadding;

		return this.getAppearance() != 'none';
	}

	getChildrenPadding(): boolean {
		if (this.props.childrenPadding != null) return this.props.childrenPadding;

		return this.props.layout == 'equal';
	}

	isSidebarLayout(): boolean {
		return this.props.layout == 'sidebar-left' || this.props.layout == 'sidebar-right';
	}
}
